import './style/globalStyle.scss';

// eslint-disable-next-line import/no-unresolved,import/no-webpack-loader-syntax
import NotFound from 'async!./routes/notFound';
import ContactUsModalLoader from 'Components/async/ContactUsModalLoader';
import { Box, Grommet } from 'grommet';
import { grommet } from 'grommet/themes';
import { deepMerge } from 'grommet/utils';
import { useCallback, useEffect, useErrorBoundary } from 'preact/hooks';
import Router from 'preact-router';
import { connect, Provider, useDispatch } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { initializeGA, logPageViewGA } from 'Shared/ga';

import DefaultLandingRedirect from '@/components/LayoutDetector/DefaultLandingRedirect';
import SpeedyFlow from '@/components/SpeedyFlow';
import { PRIVACY_PDF_URL, TERMS_PDF_URL } from '@/components/TermsAndConditions/constants';
import UserSessionSync from '@/components/UserSessionSync';
import { setReferralData } from '@/redux/slices/user/actions';
import TermsAndPrivacy from '@/routes/TermsAndPrivacy';

import HabitatsUpdater from './components/HabitatsUpdater';
import DisableJSRoutingProvider from './components/providers/DisableJSRoutingProvider';
import zoolifeTheme from './grommetTheme';
import { logAndGetCampaignData } from './helpers';
import QueryProvider from './query-provider';
import persistor from './redux/persistor';
import store from './redux/store';
import Album from './routes/album';
import Gift from './routes/gift';
import PartnerDonation from './routes/partnerDonation';
import { initializeMP } from './shared/mp';

const theme = deepMerge(grommet, zoolifeTheme);

const Content = ({ showContactUs }) => {
  const dispatch = useDispatch();
  useEffect(() => {
    const updateRefData = async () => {
      const campaignData = await logAndGetCampaignData();
      dispatch(setReferralData(campaignData));
    };
    updateRefData();
  }, [dispatch]);

  const routerChangeHandler = useCallback((props) => {
    const { url } = props;
    logPageViewGA(url);
    if (typeof window !== 'undefined' && window?.fbq) {
      window.fbq('track', 'PageView');
    }
  }, []);

  useEffect(() => {
    initializeGA();
    initializeMP();
    if (typeof window !== 'undefined') {
      logPageViewGA(window.location.pathname);
    }
  }, []);

  const routes = (
    <Box fill className="calculated-full-height">
      <Router onRouteChange={routerChangeHandler}>
        {/* fullPage: /home and /highlights live in the main bundle, not this router */}
        <DefaultLandingRedirect path="/" fullPage />

        <Gift path="/gift" title="Gift Cards" />

        <PartnerDonation path="/partnerDonation" title="Partner Donation" />

        <Album mediaType="photos" path="/album/photos/:photoId" />
        <Album mediaType="videos" path="/album/videos/:videoId" />

        <TermsAndPrivacy path="/terms-and-conditions" title="Terms and Conditions" pdfLink={TERMS_PDF_URL} />
        <TermsAndPrivacy path="/privacy-policy" title="Privacy Policy" pdfLink={PRIVACY_PDF_URL} />

        {/* NOTE: NotFound need to be at the end */}
        <NotFound path=":*" />
      </Router>

      <ContactUsModalLoader isOpen={showContactUs} />
      {/* Only `/` and `/album` resolved the session before, so a signed-in visitor looked signed
          out on the other public pages — and any gated action would have offered them an account
          they already have. */}
      <UserSessionSync />
      <SpeedyFlow />
      <HabitatsUpdater />
    </Box>
  );

  return (
    <Grommet full theme={theme} style={{ background: 'transparent' }}>
      {routes}
    </Grommet>
  );
};

const ConnectedContent = connect(
  ({
    modals: {
      contactus: { isOpen: showContactUs },
    },
  }) => ({ showContactUs }),
)(Content);

const PublicPages = () => {
  useErrorBoundary((err) => {
    console.error(err);
    // TODO: log this somewhere (sentry, logRocket, or GA)
  });

  return (
    <QueryProvider>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <DisableJSRoutingProvider>
            <ConnectedContent />
          </DisableJSRoutingProvider>
        </PersistGate>
      </Provider>
    </QueryProvider>
  );
};

export default PublicPages;
